<template>
  <div class="page-content">
    <div class="page-header">
      <h1 class="page-title">Récapitulatif des ventes</h1>
    </div>

    <!-- Filter bar -->
    <v-card flat class="table-card pa-3 mb-4">
      <div class="d-flex align-center flex-wrap" style="gap: 12px;">
        <v-select
          :items="stores"
          item-value="id"
          item-text="name"
          placeholder="Tous les emplacements"
          prepend-inner-icon="mdi-store-outline"
          v-model="store_id"
          outlined dense hide-details clearable
          style="max-width: 220px;"
          @change="reload"
        ></v-select>
        <v-btn icon @click="shiftWeek(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
        <strong>{{ periodLabel }}</strong>
        <v-btn icon :disabled="!canGoNext" @click="shiftWeek(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>

        <v-spacer></v-spacer>

        <v-menu :close-on-content-click="false" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn outlined small v-on="on"><v-icon left small>mdi-clock-outline</v-icon>{{ timeLabel }}</v-btn>
          </template>
          <v-card class="pa-3" min-width="260">
            <v-radio-group v-model="timeMode" @change="reload" dense hide-details>
              <v-radio label="Toute la journée" value="all"></v-radio>
              <v-radio label="Période personnalisée" value="custom"></v-radio>
            </v-radio-group>
            <div v-if="timeMode === 'custom'" class="d-flex" style="gap: 8px;">
              <v-select v-model="startTime" :items="hourOptions" label="Commencer" dense @change="reload"></v-select>
              <v-select v-model="endTime" :items="hourOptions" label="Fin" dense @change="reload"></v-select>
            </div>
          </v-card>
        </v-menu>

        <v-btn small text color="primary" @click="exportPDF"><v-icon left small>mdi-file-pdf-box</v-icon>PDF</v-btn>
        <v-btn small text color="green" @click="exportExcel"><v-icon left small>mdi-file-excel</v-icon>Excel</v-btn>
      </div>
    </v-card>

    <!-- Summary cards -->
    <v-row dense class="mb-2">
      <v-col v-for="m in metrics" :key="m.key" cols="6" md="">
        <v-card flat outlined class="pa-3 metric-card" :class="{ active: selectedMetric === m.key }" @click="selectedMetric = m.key">
          <div class="metric-label">{{ m.label }}</div>
          <div class="metric-value">{{ currencySymbol }}{{ metricTotal(m.key) | formatNumber }}</div>
          <div class="metric-delta" :class="deltaClass(m.key)">
            {{ deltaSign(m.key) }}{{ currencySymbol }}{{ Math.abs(metricDelta(m.key)) | formatNumber }} ({{ deltaPct(m.key) }})
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Area chart -->
    <v-card flat outlined class="pa-3 mb-4">
      <div class="mb-2"><strong>{{ selectedMetricLabel }}</strong></div>
      <div class="chart-wrapper">
        <line-chart v-if="chartReady" ref="chart" :chart-data="chartData" :options="chartOptions" />
      </div>
    </v-card>

    <!-- Daily table -->
    <v-card flat class="table-card">
      <v-simple-table>
        <thead>
          <tr>
            <th>Date</th>
            <th v-for="m in tableMetrics" :key="m.key" class="text-right">{{ m.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in days" :key="row.date">
            <td>{{ formatDay(row.date) }}</td>
            <td v-for="m in tableMetrics" :key="m.key" class="text-right">{{ currencySymbol }}{{ rowValue(row, m.key) | formatNumber }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th>TOTAL</th>
            <th v-for="m in tableMetrics" :key="m.key" class="text-right">{{ currencySymbol }}{{ metricTotal(m.key) | formatNumber }}</th>
          </tr>
        </tfoot>
      </v-simple-table>
    </v-card>
  </div>
</template>

<script>
import { Line } from 'vue-chartjs'
import moment from 'moment'
import { exportSalesSummaryPDF, exportSalesSummaryExcel } from '../utils/salesSummaryExport'

export default {
  name: 'SalesSummaryReportView',
  components: {
    LineChart: {
      extends: Line,
      props: ['chartData', 'options'],
      mounted() { setTimeout(() => this.renderChart(this.chartData, this.options), 0) },
    },
  },
  data: () => ({
    filter_rate_id: 1,
    stores: [],
    store_id: null,
    windowEnd: moment().startOf('day'),
    timeMode: 'all',
    startTime: '08:00',
    endTime: '18:00',
    selectedMetric: 'ventes_brutes',
    days: [],
    totals: {},
    previousTotals: {},
    chartReady: false,
    // Cards + chart (5, no COGS).
    metrics: [
      { key: 'ventes_brutes', label: 'Ventes brutes' },
      { key: 'remboursements', label: 'Remboursements' },
      { key: 'reductions', label: 'Réductions' },
      { key: 'ventes_nettes', label: 'Ventes nettes' },
      { key: 'marge', label: 'Marge brute' },
    ],
    // Table columns (6, includes Coût des marchandises).
    tableMetrics: [
      { key: 'ventes_brutes', label: 'Ventes brutes' },
      { key: 'remboursements', label: 'Remboursements' },
      { key: 'reductions', label: 'Réductions' },
      { key: 'ventes_nettes', label: 'Ventes nettes' },
      { key: 'cout', label: 'Coût des marchandises' },
      { key: 'marge', label: 'Marge brute' },
    ],
    hourOptions: Array.from({ length: 24 }, (_, h) => (h < 10 ? '0' + h : '' + h) + ':00'),
  }),
  computed: {
    suffix() { return this.filter_rate_id == 2 ? 'usd' : 'htg' },
    currencySymbol() { return this.filter_rate_id == 2 ? '$' : 'G' },
    windowStart() { return this.windowEnd.clone().subtract(6, 'days') },
    canGoNext() { return this.windowEnd.isBefore(moment().startOf('day'), 'day') },
    periodLabel() { return this.windowStart.format('DD MMM') + ' – ' + this.windowEnd.format('DD MMM YYYY') },
    timeLabel() { return this.timeMode === 'all' ? '00:00 – 23:59' : this.startTime + ' – ' + this.endTime },
    selectedMetricLabel() { return (this.metrics.find(m => m.key === this.selectedMetric) || {}).label },
    chartData() {
      const asc = [...this.days].sort((a, b) => a.date.localeCompare(b.date))
      return {
        labels: asc.map(d => moment(d.date).format('DD MMM')),
        datasets: [{
          label: this.selectedMetricLabel,
          data: asc.map(d => this.rowValue(d, this.selectedMetric)),
          backgroundColor: 'rgba(25, 65, 115, 0.10)',
          borderColor: '#194173',
          borderWidth: 2,
          pointBackgroundColor: '#194173',
          pointRadius: 4,
          fill: true,
          tension: 0.4,
        }],
      }
    },
    chartOptions() {
      return { responsive: true, maintainAspectRatio: false, legend: { display: false }, scales: { yAxes: [{ ticks: { beginAtZero: true } }] } }
    },
  },
  methods: {
    rowValue(row, key) { return row ? (row[key + '_' + this.suffix] || 0) : 0 },
    metricTotal(key) { return this.totals[key + '_' + this.suffix] || 0 },
    metricDelta(key) { return this.metricTotal(key) - (this.previousTotals[key + '_' + this.suffix] || 0) },
    deltaSign(key) { return this.metricDelta(key) >= 0 ? '+' : '-' },
    deltaClass(key) { return this.metricDelta(key) >= 0 ? 'delta-up' : 'delta-down' },
    deltaPct(key) {
      const prev = this.previousTotals[key + '_' + this.suffix] || 0
      const cur = this.metricTotal(key)
      if (!prev) return cur ? '+100%' : '0%'
      const p = ((cur - prev) / Math.abs(prev)) * 100
      return (p >= 0 ? '+' : '') + Math.round(p) + '%'
    },
    formatDay(d) { return moment(d).format('DD MMM YYYY') },
    shiftWeek(dir) { this.windowEnd = this.windowEnd.clone().add(dir * 7, 'days'); this.reload() },
    reload() {
      const params = { from: this.windowStart.format('YYYY-MM-DD'), to: this.windowEnd.format('YYYY-MM-DD') }
      if (this.timeMode === 'custom') { params.start_time = this.startTime; params.end_time = this.endTime }
      if (this.store_id) params.store_id = this.store_id
      this.chartReady = false
      this.$axios.get('/reports/sales-summary', { params })
        .then(r => {
          const res = r.data.result || {}
          this.days = res.days || []
          this.totals = res.totals || {}
          this.previousTotals = res.previous_totals || {}
          this.$nextTick(() => { this.chartReady = true })
        })
        .catch(() => {})
    },
    exportPDF() { exportSalesSummaryPDF(this.days, this.totals, this.suffix, { filename: 'recapitulatif_ventes.pdf' }) },
    exportExcel() { exportSalesSummaryExcel(this.days, this.totals, this.suffix, { filename: 'recapitulatif_ventes.xlsx' }) },
  },
  watch: {
    chartData: { handler(d) { if (this.$refs.chart) this.$refs.chart.renderChart(d, this.chartOptions) }, deep: true },
  },
  created() {
    if (localStorage.filter_rate_id) this.filter_rate_id = localStorage.filter_rate_id
    this.reload()
    try {
      const bizId = JSON.parse(localStorage.business).id
      this.$axios.get('/stores?orderasc=name&business_id=' + bizId)
        .then(r => { this.stores = r.data || [] })
    } catch (e) { /* ignore */ }
  },
}
</script>

<style scoped>
/* Keep the chart canvas fully inside its card (same pattern as HomeView). */
.chart-wrapper {
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
}
.chart-wrapper canvas {
  max-height: 100% !important;
}
.metric-card { cursor: pointer; transition: border-color .15s; }
.metric-card.active { border-color: #194173; border-width: 2px; }
.metric-label { color: #666; font-size: 13px; }
.metric-value { font-size: 22px; font-weight: 600; }
.metric-delta { font-size: 12px; }
.delta-up { color: #2e7d32; }
.delta-down { color: #c62828; }
</style>
